import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import type { ApplicationStatus } from "@tabbio/types";
import { MaterialSymbol } from "../icons/MaterialSymbol";
import { WebPageHeader } from "../common/WebPageHeader";
import { ApplicantsPagination } from "../jobs/applicants-pagination.web";
import { LibraryListSkeleton } from "../library/LibraryListSkeleton.web";
import { ApplicantDetailBody } from "../jobs/ApplicantDetailBody.web";
import { useCompanyApplications } from "../../hooks/useApplicationsApi";
import { useCompanyJobs } from "../../hooks/useCompanyJobs";
import { usePublishPageContext } from "../../lib/page-context";
import { formatRelativeDate } from "../../lib/jobs-format";
import {
  TALENT_NETWORK_DESTINATION,
  buildApplicationItemId,
  buildApplicationSelectionParam,
  parseApplicationSelectionParam,
  type ApplicationItemRef,
  type CompanyApplicationListItem,
  type CompanyApplicationsSort,
} from "../../lib/applications-contract";

/**
 * Company-wide Applications inbox — web master-detail (PRD §5.4 / §9).
 *
 * Renders INSIDE the content panel: at ≥900px a ~380px list column with the
 * detail pane beside it; below 900px the list alone, with row taps routing to
 * the full-screen `/(employer)/applications/[itemId]` detail. Search, filters,
 * sort, and the selection are URL-addressable (`?q&destination&status&sort&
 * application`); the list scroll position survives away-navigation under
 * `tabbio:applications-scroll`.
 */

const PAGE_SIZE = 20;
const WIDE_QUERY = "(min-width: 900px)";
const SCROLL_KEY = "tabbio:applications-scroll";
const LIST_COLUMN_WIDTH = 380;

type StatusTab = "all" | ApplicationStatus;

const STATUS_TABS: StatusTab[] = [
  "all",
  "applied",
  "reviewed",
  "shortlisted",
  "interview",
  "offered",
  "rejected",
  "withdrawn",
];

function useIsWide(): boolean {
  const [isWide, setIsWide] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia(WIDE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(WIDE_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsWide(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isWide;
}

function firstParam(value: string | string[] | undefined): string | null {
  const raw = Array.isArray(value) ? value[0] : value;
  return raw && raw.length ? raw : null;
}

// ─── Row primitives (visual vocabulary of EmployerApplicantsScreen.web) ──────

function Avatar({
  name,
  imageUrl,
}: {
  name?: string | null;
  imageUrl?: string | null;
}) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [imageUrl]);

  const initials =
    (name ?? "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "?";
  const showImage = Boolean(imageUrl) && !failed;

  if (showImage) {
    return (
      <img
        src={imageUrl as string}
        alt={name ?? ""}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <div
      aria-hidden
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        flexShrink: 0,
        background: "rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        fontWeight: 600,
        color: "var(--text-secondary)",
      }}
    >
      {initials}
    </div>
  );
}

function StateCard({
  title,
  subtitle,
  icon = "inbox",
  actionLabel,
  onAction,
}: {
  title: string;
  subtitle: string;
  icon?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div
      style={{
        padding: "48px 16px",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        alignItems: "center",
      }}
    >
      <span
        aria-hidden
        style={{ color: "var(--text-tertiary)", marginBottom: 4 }}
      >
        <MaterialSymbol name={icon} size={40} />
      </span>
      <span
        style={{
          fontSize: 18,
          fontWeight: 500,
          color: "var(--text-primary)",
        }}
      >
        {title}
      </span>
      <span
        style={{
          fontSize: 14,
          lineHeight: 1.5,
          color: "var(--text-secondary)",
          maxWidth: 420,
        }}
      >
        {subtitle}
      </span>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          style={{
            marginTop: 8,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 40,
            paddingInline: 18,
            borderRadius: 9999,
            border: 0,
            backgroundColor: "var(--text-primary)",
            color: "var(--bg)",
            fontSize: 14,
            fontWeight: 500,
            fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function ApplicationRow({
  item,
  selected,
  onClick,
}: {
  item: CompanyApplicationListItem;
  selected: boolean;
  onClick: () => void;
}) {
  const { t, i18n } = useTranslation(["common", "employer"]);

  const statusLabel =
    item.status === "active"
      ? t("employer:applications.status.active")
      : t(`common:jobApplicants.status.${item.status}` as never);

  const destinationLabel =
    item.destination.type === "talent_network"
      ? t("employer:applications.destinations.talentNetwork")
      : item.destination.title;

  const meta = [
    destinationLabel,
    formatRelativeDate(item.lastActivityAt, i18n.language),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={selected ? "true" : undefined}
      className="tabbio-list-row"
      style={{
        alignItems: "flex-start",
        minHeight: 72,
        padding: "14px 16px",
        width: "100%",
        ...(selected ? { background: "var(--selected-bg)" } : null),
      }}
    >
      <Avatar name={item.candidate.name} imageUrl={item.candidate.avatarUrl} />

      <div className="tabbio-list-row-body" style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
          }}
        >
          <span
            dir="auto"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 15,
              fontWeight: 500,
              color: "var(--text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "start",
            }}
          >
            {item.candidate.name}
          </span>
          <span className="tabbio-menu-badge" style={{ flexShrink: 0 }}>
            {statusLabel}
          </span>
        </div>

        {item.candidate.headline ? (
          <div className="tabbio-list-row-sub" dir="auto">
            {item.candidate.headline}
          </div>
        ) : null}

        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: "var(--text-tertiary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "start",
          }}
        >
          {meta}
        </div>
      </div>
    </button>
  );
}

// ─── Destination filter dropdown ─────────────────────────────────────────────

interface DestinationOption {
  value: string;
  label: string;
}

function DestinationFilter({
  options,
  value,
  onChange,
}: {
  options: DestinationOption[];
  value: string;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation("employer");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const active = options.find((option) => option.value === value);
  const isFiltered = value !== "all";

  return (
    <div ref={wrapRef} style={{ position: "relative", minWidth: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={isFiltered ? "tabbio-chip active" : "tabbio-chip"}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ maxWidth: 220 }}
      >
        <MaterialSymbol name="filter_alt" size={16} />
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {active?.label ?? t("applications.filters.destinationAll")}
        </span>
        <MaterialSymbol name={open ? "expand_less" : "expand_more"} size={16} />
      </button>

      {open ? (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            insetInlineStart: 0,
            zIndex: 30,
            minWidth: 220,
            maxWidth: 300,
            maxHeight: 320,
            overflowY: "auto",
            padding: 6,
            borderRadius: 16,
            background: "var(--menu-bg)",
            boxShadow: "var(--gem-menu-shadow)",
          }}
        >
          {options.map((option) => {
            const isActive = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "none",
                  background: isActive ? "var(--hover-strong)" : "transparent",
                  color: "var(--text-primary)",
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                  fontFamily: "inherit",
                  textAlign: "start",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!isActive)
                    e.currentTarget.style.background = "var(--hover)";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = "transparent";
                }}
              >
                <span
                  dir="auto"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {option.label}
                </span>
                {isActive ? <MaterialSymbol name="check" size={16} /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ─── Main screen ─────────────────────────────────────────────────────────────

export function CompanyApplicationsScreen() {
  const { t } = useTranslation(["common", "employer"]);
  const router = useRouter();
  const params = useLocalSearchParams<{
    q?: string;
    destination?: string;
    status?: string;
    sort?: string;
    application?: string;
  }>();
  const isWide = useIsWide();

  // ── Filter state, seeded from the URL once on mount ──
  const [searchInput, setSearchInput] = useState(() => firstParam(params.q) ?? "");
  const [query, setQuery] = useState(() => firstParam(params.q) ?? "");
  const [destination, setDestination] = useState<string>(() => {
    const raw = firstParam(params.destination);
    return raw ?? "all";
  });
  const [status, setStatus] = useState<StatusTab>(() => {
    const raw = firstParam(params.status);
    return raw && (STATUS_TABS as string[]).includes(raw)
      ? (raw as StatusTab)
      : "all";
  });
  const [sort, setSort] = useState<CompanyApplicationsSort>(() =>
    firstParam(params.sort) === "newest" ? "newest" : "activity",
  );
  const [page, setPage] = useState(1);

  // Selection is derived from the URL so links/back-forward stay truthful.
  const selection: ApplicationItemRef | null = useMemo(
    () => parseApplicationSelectionParam(firstParam(params.application)),
    [params.application],
  );

  // Debounced search.
  useEffect(() => {
    const timeout = setTimeout(() => setQuery(searchInput.trim()), 350);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [query, destination, status, sort]);

  // ── URL writeback (history-replace via setParams) ──
  useEffect(() => {
    const next = {
      q: query || undefined,
      destination: destination !== "all" ? destination : undefined,
      status: status !== "all" ? status : undefined,
      sort: sort !== "activity" ? sort : undefined,
    };
    const current = {
      q: firstParam(params.q) ?? undefined,
      destination: firstParam(params.destination) ?? undefined,
      status: firstParam(params.status) ?? undefined,
      sort: firstParam(params.sort) ?? undefined,
    };
    if (
      next.q === current.q &&
      next.destination === current.destination &&
      next.status === current.status &&
      next.sort === current.sort
    ) {
      return;
    }
    router.setParams(next as never);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, destination, status, sort]);

  // ── Data ──
  const listQuery = useCompanyApplications({
    query: query || undefined,
    destinations: destination !== "all" ? [destination] : undefined,
    statuses: status !== "all" ? [status] : undefined,
    sort,
    page,
    pageSize: PAGE_SIZE,
  });

  const companyJobs = useCompanyJobs();
  const roleOptions = useMemo<DestinationOption[]>(() => {
    const nonArchived = companyJobs.jobs.filter(
      (job) => job.status !== "archived" && !job.archivedAt,
    );
    return [
      { value: "all", label: t("employer:applications.filters.destinationAll") },
      ...nonArchived.map((job) => ({ value: job.id, label: job.title })),
      {
        value: TALENT_NETWORK_DESTINATION,
        label: t("employer:applications.destinations.talentNetwork"),
      },
    ];
  }, [companyJobs.jobs, t]);

  const result = listQuery.data;
  const items = result?.items ?? [];
  const total = result?.total ?? 0;
  const totalPages = result
    ? Math.max(1, Math.ceil(result.total / (result.pageSize || PAGE_SIZE)))
    : 1;
  const isFiltered =
    Boolean(query) || destination !== "all" || status !== "all";

  const clearFilters = useCallback(() => {
    setSearchInput("");
    setQuery("");
    setDestination("all");
    setStatus("all");
  }, []);

  // ── Selection ──
  const selectItem = useCallback(
    (item: CompanyApplicationListItem) => {
      if (isWide) {
        const param = buildApplicationSelectionParam(item);
        if (param) {
          router.setParams({ application: param } as never);
        }
        return;
      }
      const itemId = buildApplicationItemId(item);
      if (!itemId) return;
      const suffix =
        item.kind === "application" && item.jobId
          ? `?jobId=${encodeURIComponent(item.jobId)}`
          : "";
      router.push(`/(employer)/applications/${itemId}${suffix}` as never);
    },
    [isWide, router],
  );

  const isItemSelected = useCallback(
    (item: CompanyApplicationListItem) => {
      if (!selection) return false;
      if (selection.kind === "application") {
        return (
          item.kind === "application" &&
          item.applicationId === selection.applicationId
        );
      }
      return item.kind === "talent" && item.submissionId === selection.submissionId;
    },
    [selection],
  );

  // ── Scroll persistence (mirrors tabbio:jobs-feed-scroll) ──
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollRestoredRef = useRef(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (typeof window === "undefined" || !el) return;
    let raf = 0;
    const handler = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        try {
          window.sessionStorage.setItem(SCROLL_KEY, String(el.scrollTop));
        } catch {
          // sessionStorage may be unavailable (private mode); ignore.
        }
      });
    };
    el.addEventListener("scroll", handler, { passive: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      el.removeEventListener("scroll", handler);
    };
  }, []);

  useEffect(() => {
    if (scrollRestoredRef.current) return;
    if (listQuery.isLoading) return;
    const el = scrollRef.current;
    if (!el) return;
    scrollRestoredRef.current = true;
    let saved: string | null = null;
    try {
      saved = window.sessionStorage.getItem(SCROLL_KEY);
    } catch {
      saved = null;
    }
    if (!saved) return;
    const target = Number(saved);
    if (!Number.isFinite(target) || target <= 0) return;
    el.scrollTop = target;
  }, [listQuery.isLoading]);

  // ── Agent page context ──
  // The PageContextScreen union has no employer-specific ids yet, so the
  // inbox reports the nearest existing screen ('jobs') and carries the real
  // surface in `state`; an application selection names its job entity.
  usePublishPageContext(
    useMemo(
      () => ({
        screen: "jobs" as const,
        ...(selection?.kind === "application" && selection.jobId
          ? {
              entity: { type: "job" as const, id: selection.jobId },
            }
          : {}),
        state: {
          surface: "company-applications",
          ...(query ? { query } : {}),
          ...(destination !== "all" ? { destination } : {}),
          ...(status !== "all" ? { status } : {}),
          sort,
          ...(selection
            ? {
                selection: {
                  kind: selection.kind,
                  ...(selection.applicationId
                    ? { applicationId: selection.applicationId }
                    : {}),
                  ...(selection.submissionId
                    ? { submissionId: selection.submissionId }
                    : {}),
                },
              }
            : {}),
        },
      }),
      [selection, query, destination, status, sort],
    ),
  );

  // ── List body states (PRD §9.5) ──
  let listBody: ReactNode;
  if (listQuery.isLoading) {
    listBody = <LibraryListSkeleton count={6} />;
  } else if (listQuery.error) {
    listBody = (
      <StateCard
        icon="error_outline"
        title={t("employer:applications.error.title")}
        subtitle={t("employer:applications.error.subtitle")}
        actionLabel={t("common:retry")}
        onAction={() => listQuery.refetch()}
      />
    );
  } else if (!items.length && isFiltered) {
    listBody = (
      <StateCard
        icon="search_off"
        title={t("employer:applications.filteredEmpty.title")}
        subtitle={t("employer:applications.filteredEmpty.subtitle")}
        actionLabel={t("common:jobApplicants.clearFilters")}
        onAction={clearFilters}
      />
    );
  } else if (!items.length) {
    listBody = (
      <StateCard
        icon="inbox"
        title={t("employer:applications.empty.title")}
        subtitle={t("employer:applications.empty.subtitle")}
        actionLabel={t("employer:applications.empty.action")}
        onAction={() => router.push("/(employer)/company" as never)}
      />
    );
  } else {
    listBody = (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          opacity: listQuery.isPlaceholderData ? 0.6 : 1,
          transition: "opacity 120ms",
        }}
      >
        {items.map((item) => (
          <ApplicationRow
            key={item.id}
            item={item}
            selected={isWide && isItemSelected(item)}
            onClick={() => selectItem(item)}
          />
        ))}
      </div>
    );
  }

  const listColumn = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        ...(isWide
          ? {
              width: LIST_COLUMN_WIDTH,
              flex: "none",
              borderInlineEnd: "1px solid var(--input-border)",
            }
          : { flex: 1, minWidth: 0 }),
      }}
    >
      <div style={{ padding: "20px 16px 0" }}>
        {/* Compact inside the wide master-detail column; the full-size route
            header (with the drawer hamburger on phones) when the list IS the
            screen — compact headers never own the drawer affordance. */}
        <WebPageHeader
          title={t("employer:applications.title")}
          subtitle={
            total > 0
              ? t("employer:applications.countLabel", { count: total })
              : undefined
          }
          compact={isWide}
          hideBackOnMobile
        />

        {/* Search */}
        <div
          className="tabbio-search"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            height: 44,
            borderRadius: 9999,
            paddingInline: 16,
            border: "1px solid var(--input-border)",
            background: "var(--bg)",
            marginBottom: 10,
          }}
        >
          <MaterialSymbol
            name="search"
            size={20}
            style={{ color: "var(--text-secondary)", flexShrink: 0 }}
          />
          <input
            type="text"
            dir="auto"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={t("employer:applications.searchPlaceholder")}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              background: "transparent",
              outline: "none",
              fontSize: 14,
              color: "var(--text-primary)",
              fontFamily: "inherit",
            }}
          />
          {searchInput ? (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              aria-label={t("common:jobApplicants.filters.clear")}
              style={{
                width: 28,
                height: 28,
                border: 0,
                borderRadius: "50%",
                background: "transparent",
                color: "var(--text-tertiary)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MaterialSymbol name="close" size={18} />
            </button>
          ) : null}
        </div>

        {/* Destination + sort */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10,
            flexWrap: "wrap",
          }}
        >
          <DestinationFilter
            options={roleOptions}
            value={destination}
            onChange={setDestination}
          />
          <button
            type="button"
            className="tabbio-chip"
            onClick={() =>
              setSort((current) =>
                current === "activity" ? "newest" : "activity",
              )
            }
            aria-label={t("employer:applications.sort.label")}
          >
            <MaterialSymbol name="swap_vert" size={16} />
            {sort === "activity"
              ? t("employer:applications.sort.activity")
              : t("employer:applications.sort.newest")}
          </button>
          {isFiltered ? (
            <button
              type="button"
              onClick={clearFilters}
              style={{
                border: 0,
                background: "transparent",
                color: "var(--text-secondary)",
                fontSize: 13,
                fontWeight: 500,
                fontFamily: "inherit",
                cursor: "pointer",
                paddingInline: 4,
              }}
            >
              {t("common:jobApplicants.clearFilters")}
            </button>
          ) : null}
        </div>

        {/* Status chips */}
        <div
          style={{
            display: "flex",
            gap: 8,
            overflowX: "auto",
            padding: "2px 0 12px",
            scrollbarWidth: "none",
          }}
        >
          {STATUS_TABS.map((tab) => {
            const active = status === tab;
            const label =
              tab === "all"
                ? t("common:jobApplicants.filters.all")
                : t(`common:jobApplicants.status.${tab}` as never);
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setStatus(tab)}
                className={active ? "tabbio-chip active" : "tabbio-chip"}
                style={{ flexShrink: 0 }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scrolling list */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "4px 16px 24px",
        }}
      >
        {listBody}
        {totalPages > 1 ? (
          <ApplicantsPagination
            page={result?.page ?? page}
            totalPages={totalPages}
            onChange={(next) => {
              setPage(next);
              scrollRef.current?.scrollTo({ top: 0 });
            }}
          />
        ) : null}
      </div>
    </div>
  );

  const detailPane = isWide ? (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      {selection ? (
        <ApplicantDetailBody
          key={
            selection.kind === "application"
              ? `a:${selection.applicationId}`
              : `t:${selection.submissionId}`
          }
          jobId={selection.jobId ?? null}
          applicationId={selection.applicationId}
          submissionId={selection.submissionId}
          variant="pane"
          onStatusChanged={() => listQuery.refetch()}
        />
      ) : (
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
          }}
        >
          <StateCard
            icon="contact_page"
            title={t("employer:applications.detail.selectPrompt.title")}
            subtitle={t("employer:applications.detail.selectPrompt.subtitle")}
          />
        </div>
      )}
    </div>
  ) : null;

  return (
    <div
      className="h-full"
      style={{
        display: "flex",
        height: "100%",
        minHeight: 0,
        backgroundColor: "var(--bg)",
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      {listColumn}
      {detailPane}
    </div>
  );
}
